import React, { useState } from 'react';
import { AlertCircle, X, User } from 'lucide-react';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import Input from '../ui/Input';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../store/authStore';
import { toast } from 'sonner';

interface IncompleteProfileBannerProps {
  firstName: string | null;
  lastName: string | null;
}

const IncompleteProfileBanner: React.FC<IncompleteProfileBannerProps> = ({
  firstName,
  lastName
}) => {
  const [dismissed, setDismissed] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    firstName: firstName || '',
    lastName: lastName || '',
    secondLastName: ''
  });
  const [saving, setSaving] = useState(false);

  const { user } = useAuthStore();

  const isIncomplete = !firstName || !lastName;

  if (!isIncomplete || dismissed) {
    return null;
  }

  const handleSave = async () => {
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      toast.error('Por favor completa tu nombre y primer apellido');
      return;
    }

    if (!user) return;

    setSaving(true);

    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({
          first_name: formData.firstName.trim(),
          last_name: formData.lastName.trim(),
          second_last_name: formData.secondLastName.trim() || null,
          full_name: `${formData.firstName.trim()} ${formData.lastName.trim()}${formData.secondLastName ? ' ' + formData.secondLastName.trim() : ''}`.trim(),
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (error) throw error;

      toast.success('Perfil actualizado correctamente');
      setShowModal(false);
      setDismissed(true);

      // Reload page to refresh user data everywhere
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } catch (error: any) {
      console.error('[IncompleteProfileBanner] Error updating profile:', error);
      toast.error('Error al actualizar el perfil');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="mb-6 bg-amber-50 border-2 border-amber-200 rounded-lg p-4 shadow-sm">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <AlertCircle className="h-6 w-6 text-amber-600" />
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-amber-900 mb-1">
              Completa tu perfil
            </h3>
            <p className="text-sm text-amber-800 mb-3">
              Añade tu nombre para que tu familia te reconozca.
            </p>

            <div className="flex items-center space-x-2">
              <Button
                onClick={() => setShowModal(true)}
                size="sm"
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                <User className="h-4 w-4 mr-1" />
                Completar Perfil
              </Button>

              <button
                onClick={() => setDismissed(true)}
                className="text-sm text-amber-700 hover:text-amber-800 underline"
              >
                Más tarde
              </button>
            </div>
          </div>

          <button
            onClick={() => setDismissed(true)}
            className="flex-shrink-0 text-amber-400 hover:text-amber-600 transition-colors"
            aria-label="Cerrar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      <Modal
        isOpen={showModal}
        onClose={() => !saving && setShowModal(false)}
        title="Completa tu perfil"
      >
        <div className="space-y-4">
          <p className="text-sm text-stone-600">
            Añade tu nombre para que tu familia te reconozca.
          </p>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Nombre <span className="text-red-500">*</span>
            </label>
            <Input
              type="text"
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              placeholder="Ej: María"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Primer Apellido <span className="text-red-500">*</span>
            </label>
            <Input
              type="text"
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              placeholder="Ej: González"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Segundo Apellido (opcional)
            </label>
            <Input
              type="text"
              value={formData.secondLastName}
              onChange={(e) => setFormData({ ...formData, secondLastName: e.target.value })}
              placeholder="Ej: López"
            />
          </div>

          <div className="flex space-x-2 pt-2">
            <Button
              onClick={handleSave}
              disabled={saving}
              fullWidth
            >
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowModal(false)}
              disabled={saving}
              fullWidth
            >
              Cancelar
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default IncompleteProfileBanner;
